import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { getLeaderboard, getMyLeaderboardRank } from "@/lib/leaderboard.functions";
import { supabase } from "@/integrations/supabase/client";
import { resolveImage } from "@/lib/image-map";
import { RouteError, RouteNotFound } from "@/components/route-fallbacks";
import { SITE_URL } from "@/lib/utils";
import { ArrowRight } from "lucide-react";

const leaderboardQueryOptions = () =>
  queryOptions({
    queryKey: ["leaderboard"],
    queryFn: () => getLeaderboard(),
    staleTime: 1000 * 60 * 60, // 1 hour
  });

export const Route = createFileRoute("/leaderboard")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(leaderboardQueryOptions()),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  head: () => ({
    meta: [
      { title: "Creator Leaderboard — Top Earning Creators | Pluggz" },
      {
        name: "description",
        content:
          "See the top earning creators on Pluggz. Shop their curated storefronts and discover what's trending.",
      },
      { property: "og:title", content: "Creator Leaderboard — Top Earning Creators | Pluggz" },
      {
        property: "og:description",
        content:
          "See the top earning creators on Pluggz. Shop their curated storefronts and discover what's trending.",
      },
      { property: "og:url", content: `${SITE_URL}/leaderboard` },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/leaderboard` }],
  }),
  component: LeaderboardPage,
});

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(amount);
}

function LeaderboardPage() {
  const { data: entries } = useSuspenseQuery(leaderboardQueryOptions());
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setUserId(s?.user?.id ?? null));
    return () => sub.subscription.unsubscribe();
  }, []);

  const fetchRank = useServerFn(getMyLeaderboardRank);
  const myRank = useQuery({
    queryKey: ["leaderboard-my-rank", userId],
    queryFn: () => fetchRank(),
    enabled: !!userId,
    staleTime: 60_000,
  });

  const allZero = entries.length === 0 || entries.every((e) => e.total_commission === 0);
  const myRankNum = myRank.data?.rank ?? null;
  const inTop = !!myRankNum && myRankNum <= entries.length;

  return (
    <div className="mx-auto max-w-3xl px-6 py-16 md:py-24">
      <div className="text-center">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
          Pluggz Rankings
        </p>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl">Top Creators</h1>
        <p className="mx-auto mt-4 max-w-lg text-muted-foreground">
          The highest-earning creators on Pluggz, ranked by total commissions.
        </p>
      </div>

      <div className="mt-14 space-y-4">
        {allZero && (
          <div className="border border-border bg-card px-6 py-14 text-center">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Early access</p>
            <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
              The leaderboard will populate once creators start earning commissions. Be the first!
            </p>
            <Link
              to="/apply"
              className="mt-6 inline-flex items-center gap-2 border border-border bg-foreground px-4 py-2 text-xs uppercase tracking-widest text-background transition hover:opacity-90"
            >
              Apply now <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        )}

        {!allZero && entries.map((entry) => (
          <div
            key={entry.creator_id}
            className="group flex items-center gap-4 border border-border bg-card p-4 transition hover:border-foreground/30"
          >
            <span
              className={`flex h-8 w-8 shrink-0 items-center justify-center text-xs font-bold ${
                entry.position <= 3
                  ? "bg-foreground text-background"
                  : "border border-border text-muted-foreground"
              }`}
            >
              {entry.position}
            </span>

            <img
              src={resolveImage(entry.avatar_url)}
              alt={entry.name}
              className="h-12 w-12 rounded-full object-cover"
            />

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-serif text-base">{entry.name}</p>
                <span className="text-xs text-muted-foreground">
                  @{entry.handle}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                {entry.category && (
                  <span className="uppercase tracking-widest">
                    {entry.category}
                  </span>
                )}
                <span className="font-medium text-foreground">
                  {formatCurrency(entry.total_commission)} earned
                </span>
              </div>
            </div>

            <Link
              to="/creator/$handle"
              params={{ handle: entry.handle }}
              className="inline-flex shrink-0 items-center gap-1 border border-border px-3 py-2 text-[11px] uppercase tracking-widest transition hover:bg-foreground hover:text-background"
            >
              Shop their picks
              <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        ))}

        {userId && myRank.data && !inTop && (
          <div className="mt-8 flex items-center justify-between border border-border bg-card p-4">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Your standing</p>
              <p className="mt-1 font-serif text-lg">
                Your rank: {myRank.data.rank ? `#${myRank.data.rank}` : "Unranked"}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(myRank.data.total)} earned
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
